import SubscriptionPlan from "./internal-subscription-plan/SubscriptionPlan";
import InvoiceSummary from "./internal-subscription-plan/InvoiceSummary";
import WidgetContainer from "components/common/WidgetContainer";
import Loader from "components/common/controls/Loader";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { useEffect, useState } from "react";
import { getSubscription } from "store/slices/SubscriptionSlice";
import Subscription from "interfaces/response/Subscription";
import { getInvoices } from "store/slices/InvoiceSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { getLastInvoice } from "shared/utility/InvoiceHelper";
import Invoice from "interfaces/response/Invoice";
import { getSelectedProductSubscriptionId } from "shared/utility/ProductSubscriptionHelper";
import NextPaymentDetails from "interfaces/response/NextPaymentDetails";
import { getNextPaymentDetails } from "shared/utility/ProductSubscriptionHelper";
import { useLocation } from "react-router-dom";

interface Props {
  dispatch?: AppDispatch;
  currentProductSubscriptionId: number;
  currentSubscription?: Subscription | null;
  lastInvoice: Invoice | null;
  nextPaymentDetails: NextPaymentDetails;
}

export function SubscriptionManager(props: Props) {
  const {
    dispatch,
    currentSubscription,
    currentProductSubscriptionId,
    lastInvoice,
    nextPaymentDetails,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isNewSubscriptionDataLoaded, setIsNewSubscriptionDataLoaded] = useState(false);

  useEffect(() => {
    const loadData = () => {
      if (currentProductSubscriptionId) {
        setIsLoading(true);
        dispatch?.(getSubscription(currentProductSubscriptionId))
          .then(unwrapResult)
          .then((subscription: Subscription) => {
            if (!subscription.isOfflineSubscription) {
              return dispatch(getInvoices(subscription.id));
            }
          })
          .finally(() => {
            setIsLoading(false);
            setIsDataLoaded(true);
          });
      }
    };

    loadData();
  }, [currentProductSubscriptionId, dispatch]);

  const queryParams = useLocation().search;

  useEffect(() => {
    const success = new URLSearchParams(queryParams).get("isSuccess");

    // When we are creating a new subscription, we will forcefully wait for 2 seconds if payment is successful.
    // Because sometimes it may happen that the plan is changed but it is not showing in the ui immediately.
    // For this reason we are waiting for 2 seconds. 
    if (success === "true") {
      setTimeout(() => {
        setIsNewSubscriptionDataLoaded(true);
      }, 2000);
    }
    else {
      setIsNewSubscriptionDataLoaded(true);
    }
  }, [queryParams]);

  return (
    <>
      {isNewSubscriptionDataLoaded && (
        <>
          <WidgetContainer>
            <SubscriptionPlan
              currentSubscription={currentSubscription!}
              nextPaymentDetails={nextPaymentDetails}
            />
          </WidgetContainer>
        
          {lastInvoice && isDataLoaded && (
            <WidgetContainer>
              <InvoiceSummary
                invoice={lastInvoice}
                nextPaymentDetails={nextPaymentDetails}
              />
            </WidgetContainer>
          )}
        </>  
      )}

      <Loader isVisible={isLoading || !isNewSubscriptionDataLoaded} />
    </>
  );
}

const mapStateToProps = (state: RootState, props: Props) => {
  return {
    ...props,
    currentProductSubscriptionId: getSelectedProductSubscriptionId(state),
    currentSubscription: state.subscriptionData.selectedProductSubscription,
    lastInvoice: getLastInvoice(state),
    nextPaymentDetails: getNextPaymentDetails(state),
  };
};

export default connect(mapStateToProps)(SubscriptionManager);
